import React, { useState, useEffect } from "react";
import { X, Trash2 } from "lucide-react";
import { masterService } from "../../api/masterService";
import SearchableSelect from "../SearchableSelect";

export default function RequestDetailModal({
  isOpen,
  onClose,
  data,
  onUpdateStatus,
  loading,
}) {
  const [units, setUnits] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loadingMasterData, setLoadingMasterData] = useState(false);
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (isOpen) {
      fetchMasterData();
    }
  }, [isOpen]);

  useEffect(() => {
    // Initialize items when data or master data is loaded
    if (data && data.items && units.length > 0 && brands.length > 0) {
      setItems(
        data.items.map((item) => {
          const selectedUnit = item.unit_id || item.asset_unit_id || "";
          const selectedBrand = item.brand_id || "";

          return {
            ...item,
            selectedUnit,
            selectedBrand,
          };
        }),
      );
    } else if (data && data.items) {
      // Set items even if master data not loaded yet
      setItems(
        data.items.map((item) => ({
          ...item,
          selectedUnit: item.unit_id || item.asset_unit_id || "",
          selectedBrand: item.brand_id || "",
        })),
      );
    }
  }, [data, units, brands]);

  const fetchMasterData = async () => {
    try {
      setLoadingMasterData(true);
      const [unitsRes, brandsRes] = await Promise.all([
        masterService.assetUnits.list({ status: "ACTIVE" }),
        masterService.brands.list({ status: "ACTIVE" }),
      ]);

      setUnits(unitsRes.data || []);
      setBrands(brandsRes.data || []);
    } catch (error) {
      console.error("Error fetching master data:", error);
    } finally {
      setLoadingMasterData(false);
    }
  };

  const updateItem = (index, field, value) => {
    setItems(
      items.map((item, idx) =>
        idx === index ? { ...item, [field]: value } : item,
      ),
    );
  };

  const handleDeleteItem = (index) => {
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
  };

  const handleApprove = () => {
    if (onUpdateStatus) {
      // Prepare items for API with updated unit and brand selections
      const formattedItems = items.map((item) => ({
        id: item.id,
        product_id: item.product_id,
        variant_id: item.variant_id,
        unit_id: item.selectedUnit,
        brand_id: item.selectedBrand,
        qty_ordered: item.qty_ordered || item.quantity,
      }));
      onUpdateStatus("APPROVED", formattedItems);
    }
  };

  if (!isOpen || !data) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto overflow-x-hidden p-4 sm:p-0">
      {/* Backdrop */}
      <div
        className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 modal-backdrop-enter"
        onClick={onClose}></div>

      {/* Modal Panel */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl sm:my-8 sm:w-full sm:max-w-[1100px] modal-panel-enter">
        {/* Header */}
        <div className="px-6 py-6 border-b border-gray-200 flex justify-between items-start">
          <div>
            <h3 className="text-xl font-semibold leading-6 text-gray-900">
              Purchase Order
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Buat permintaan pengadaan atau pembelian produk ke supplier
            </p>
          </div>
          <button
            type="button"
            className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
            onClick={onClose}>
            <span className="sr-only">Close</span>
            <X className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-6">
          {/* Supplier Info & Add Product Button */}
          <div className="mb-6 flex justify-between items-center">
            <div className="flex items-center border border-gray-200 rounded px-4 py-2 min-w-[260px] bg-white">
              <span className="text-sm font-medium text-gray-700 flex-grow">
                {data.supplier?.name || "-"}
              </span>
              {/* Close icon for supplier (visual only based on design) */}
              <X className="w-4 h-4 text-red-500 ml-2 cursor-pointer" />
            </div>

            <button className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500">
              Tambah Produk
            </button>
          </div>

          {/* Table */}
          <div className="border border-gray-200 rounded-lg overflow-hidden mb-6">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                    Aksi
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Produk
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48">
                    Unit
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48">
                    Merek
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                    Jumlah
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                    Satuan
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {items.length === 0 ? (
                  <tr>
                    <td
                      colSpan="6"
                      className="px-6 py-4 text-center text-sm text-gray-500">
                      Tidak ada item
                    </td>
                  </tr>
                ) : (
                  items.map((item, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleDeleteItem(index)}
                          className="p-2 bg-red-400 text-white rounded hover:bg-red-500 transition disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={
                            data.status === "REJECTED" ||
                            data.status === "APPROVED"
                          }>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {item.product_name || "-"}
                      </td>
                      <td className="px-4 py-3">
                        {loadingMasterData ? (
                          <div className="text-sm text-gray-500 italic">
                            Loading...
                          </div>
                        ) : (
                          <SearchableSelect
                            options={units}
                            value={item.selectedUnit || ""}
                            onChange={(value) =>
                              updateItem(index, "selectedUnit", value)
                            }
                            fetchOptions={async (term) => {
                              const response =
                                await masterService.assetUnits.list({
                                  status: "ACTIVE",
                                  query: term,
                                });
                              return response.data || [];
                            }}
                            minSearchLength={1}
                            placeholder="Pilih salah satu unit..."
                            disabled={
                              data.status === "REJECTED" ||
                              data.status === "APPROVED"
                            }
                            displayKey="name"
                            valueKey="id"
                          />
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {loadingMasterData ? (
                          <div className="text-sm text-gray-500 italic">
                            Loading...
                          </div>
                        ) : (
                          <SearchableSelect
                            options={brands}
                            value={item.selectedBrand || ""}
                            onChange={(value) =>
                              updateItem(index, "selectedBrand", value)
                            }
                            fetchOptions={async (term) => {
                              const response = await masterService.brands.list({
                                status: "ACTIVE",
                                query: term,
                              });
                              return response.data || [];
                            }}
                            minSearchLength={1}
                            placeholder="Pilih merek..."
                            disabled={
                              data.status === "REJECTED" ||
                              data.status === "APPROVED"
                            }
                            displayKey="name"
                            valueKey="id"
                          />
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="number"
                          value={item.qty_ordered ?? item.quantity ?? 0}
                          onChange={(e) =>
                            updateItem(index, "quantity", e.target.value)
                          }
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 sm:text-sm border p-2"
                          min="1"
                          disabled={
                            data.status === "REJECTED" ||
                            data.status === "APPROVED"
                          }
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.product_unit_name ||
                          item.unit_measurement ||
                          "Pcs"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Footer Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
            <button
              type="button"
              className="inline-flex justify-center rounded-md border border-transparent bg-red-600 px-6 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => onUpdateStatus("REJECTED")}
              disabled={
                loading ||
                data.status === "REJECTED" ||
                data.status === "APPROVED"
              }>
              Tolak
            </button>
            <button
              type="button"
              className="inline-flex justify-center rounded-md border border-transparent bg-green-600 px-6 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleApprove}
              disabled={
                loading ||
                data.status === "APPROVED" ||
                data.status === "REJECTED"
              }>
              Setujui Permintaan
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
